import { useRef, useState } from 'react';

import { ALREADY_ON_LIST_MESSAGE } from '@/hooks/useAllowlist';

// Design: Beta Users Admin — "Add a user" card. Validation order matches
// the prototype: format first, then a client-side duplicate check; the
// server's duplicate 400 lands in the same inline slot via `onAdd`.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AddUserCardProps {
  existingEmails: string[];
  // Resolves to null on success, or the inline error message to show.
  onAdd: (email: string) => Promise<string | null>;
}

export function AddUserCard({ existingEmails, onAdd }: AddUserCardProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleAdd() {
    const email = value.trim().toLowerCase();
    if (!email || adding) return;

    if (!EMAIL_REGEX.test(email)) {
      setError('Enter a valid email address');
      return;
    }
    if (existingEmails.includes(email)) {
      setError(ALREADY_ON_LIST_MESSAGE);
      return;
    }

    setAdding(true);
    const failure = await onAdd(email);
    setAdding(false);
    if (failure !== null) {
      setError(failure);
      return;
    }
    setValue('');
    setError(null);
    inputRef.current?.focus();
  }

  const canAdd = value.trim().length > 0 && !adding;

  return (
    <div className="bg-wolisho-card rounded-xl p-4 mb-5" data-testid="add-user-card">
      <p className="text-[13px] font-bold text-wolisho-brown mb-2.5">Add a user</p>
      <div className="flex items-stretch gap-2">
        <input
          ref={inputRef}
          type="email"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleAdd();
            }
          }}
          placeholder="email@example.com"
          disabled={adding}
          data-testid="add-user-input"
          data-ph-mask
          className={`min-w-0 flex-1 rounded-lg border bg-wolisho-cream px-3 py-2.5 text-sm text-black outline-hidden transition-colors ${
            error ? 'border-wolisho-red' : 'border-wolisho-brown/20'
          } ${adding ? 'opacity-60' : ''}`}
        />
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={!canAdd}
          data-testid="add-user-button"
          className={`flex shrink-0 items-center gap-1.5 rounded-lg px-[18px] text-sm font-bold transition-colors ${
            canAdd
              ? 'bg-wolisho-red text-white'
              : 'cursor-not-allowed bg-wolisho-brown/[0.08] text-wolisho-brown/30'
          }`}
        >
          {adding ? (
            <span
              className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
              role="status"
              aria-label="Adding"
            />
          ) : (
            <>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Add</span>
            </>
          )}
        </button>
      </div>
      {error && (
        <p className="mt-1.5 text-xs font-bold text-wolisho-red" data-testid="add-user-error">
          {error}
        </p>
      )}
    </div>
  );
}
